import { selectDefaultRootURL, useSelector } from '@missionlabs/api'
import { FormProvider, useYupForm, yup } from '@missionlabs/react'
import { AddressLookup } from '@missionlabs/types'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import clWelcomeImage from 'assets/images/cl-welcome-background.png'
import plWelcomeImage from 'assets/images/pl-welcome-background.png'
import { userAddressSchema } from 'features/settings/pages/MyDetails/MyDetails'
import { FC, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { DocumentTitle } from 'shared/components/DocumentTitle'
import { useGetResetTokenQuery, useUpdateResetTokenMutation } from 'shared/store'
import { AppConfig, isPhoneLinePlus } from 'shared/utils/env'

import { WelcomeError } from './WelcomeError'
import { WelcomeForm } from './WelcomeForm'
import { WelcomeSuccess } from './WelcomeSuccess'

export interface WelcomeProps {}

export interface WelcomeFields {
    password: string
    acceptTerms?: boolean
    address?: AddressLookup
}

export const Welcome: FC<WelcomeProps> = () => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const tokenID = useMemo(() => searchParams.get('token'), [searchParams])

    // The API URL isn't known at this point because we're not logged in, also
    // useUpdateResetTokenMutation expects the endpoint without "/admin"
    const baseUrl = useSelector(selectDefaultRootURL)

    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const { data: token, isError } = useGetResetTokenQuery(tokenID || skipToken)
    const [activateAccount, { isSuccess }] = useUpdateResetTokenMutation()

    const methods = useYupForm<WelcomeFields>(
        yup.object({
            password: yup.string().required(),
            address: userAddressSchema
        }),
        { defaultValues: { password: '' }, mode: 'onChange' }
    )
    const {
        formState: { isSubmitting, isValid }
    } = methods

    const onSubmit = async (data: WelcomeFields) => {
        try {
            if (!tokenID) throw Error('Missing required search params.')

            console.warn(`Activate:\n\n${JSON.stringify(data, null, 4)}`)
            await activateAccount({
                ID: tokenID,
                endpoint: baseUrl,
                password: data.password,
                address: data.address
            }).unwrap()
        } catch (e) {
            console.error(e)
            setErrorMessage('data' in e ? e.data.message : e.message)
        }
    }

    const backgroundImage = isPhoneLinePlus ? plWelcomeImage : clWelcomeImage

    let content: ReactNode
    if (isSuccess) {
        content = <WelcomeSuccess backgroundImage={backgroundImage} />
    } else if (isError) {
        content = <WelcomeError backgroundImage={backgroundImage} />
    } else {
        content = (
            <WelcomeForm
                backgroundImage={backgroundImage}
                errorMessage={errorMessage}
                isSubmitting={isSubmitting}
                isValid={isValid}
                token={token}
            />
        )
    }

    return (
        <>
            <DocumentTitle breadcrumbs={[t('welcome.title', { appName: AppConfig.name })]} />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>{content}</form>
            </FormProvider>
        </>
    )
}
